import classNames from 'classnames';
import type { FC } from 'react';

const SKELETON_SURFACE_CLASS = 'rounded-lg bg-slate-200 md:animate-pulse';
const DESKTOP_OFFER_SKELETON_KEYS = ['desktop-offer-1', 'desktop-offer-2', 'desktop-offer-3', 'desktop-offer-4'];
const MOBILE_OFFER_SKELETON_KEYS = ['mobile-offer-1', 'mobile-offer-2'];
const SURVEY_FILTER_SKELETON_KEYS = ['survey-filter-1', 'survey-filter-2', 'survey-filter-3'];
const SURVEY_CARD_SKELETON_KEYS = [
  'survey-card-1',
  'survey-card-2',
  'survey-card-3',
  'survey-card-4',
  'survey-card-5',
  'survey-card-6',
];

const OfferCardSkeleton: FC = () => (
  <div className="h-[230px] w-[220px] shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:w-[250px]">
    <div className={`${SKELETON_SURFACE_CLASS} h-[138px] w-full rounded-none md:h-[156px]`} />
    <div className="flex flex-col gap-3 p-4">
      <div className={`${SKELETON_SURFACE_CLASS} h-4 w-4/5`} />
      <div className={`${SKELETON_SURFACE_CLASS} h-3 w-1/2`} />
      <div className="flex gap-2">
        <div className={`${SKELETON_SURFACE_CLASS} h-6 w-20`} />
        <div className={`${SKELETON_SURFACE_CLASS} h-6 w-16`} />
      </div>
    </div>
  </div>
);

const SurveyCardSkeleton: FC<{ fullWidth?: boolean }> = ({ fullWidth = false }) => (
  <div
    className={classNames(
      'h-[115px] shrink-0 rounded-lg border border-indigo-100 bg-white p-3 shadow-sm md:h-[160px] md:w-[320px]',
      fullWidth ? 'w-full' : 'w-[280px]'
    )}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className={`${SKELETON_SURFACE_CLASS} h-10 w-10 rounded-full`} />
        <div className={`${SKELETON_SURFACE_CLASS} h-7 w-28`} />
      </div>
      <div className={`${SKELETON_SURFACE_CLASS} h-7 w-16 rounded-full`} />
    </div>
    <div className={`${SKELETON_SURFACE_CLASS} mt-4 h-px w-full`} />
    <div className="mt-5 flex items-center justify-between">
      <div className={`${SKELETON_SURFACE_CLASS} h-5 w-24`} />
      <div className={`${SKELETON_SURFACE_CLASS} h-8 w-24`} />
    </div>
  </div>
);

export const HomeOffersSkeleton: FC = () => (
  <section className="relative w-full self-stretch" aria-busy="true" aria-label="Loading offers">
    <div className="hidden w-full flex-col items-start gap-4 self-stretch md:flex">
      <div className="flex w-full items-end justify-between">
        <div className={`${SKELETON_SURFACE_CLASS} h-8 w-48`} />
        <div className="flex items-end gap-3">
          <div className={`${SKELETON_SURFACE_CLASS} h-[34px] w-36`} />
          <div className={`${SKELETON_SURFACE_CLASS} h-10 w-32`} />
          <div className={`${SKELETON_SURFACE_CLASS} h-10 w-24`} />
        </div>
      </div>
      <div className="flex w-full gap-6 overflow-hidden">
        {DESKTOP_OFFER_SKELETON_KEYS.map((key) => (
          <OfferCardSkeleton key={key} />
        ))}
      </div>
    </div>

    <div className="flex w-full min-w-0 flex-col items-start gap-3 self-stretch md:hidden">
      <div className="flex w-full flex-col gap-2 self-stretch">
        <div className="flex w-full items-center justify-between gap-2">
          <div className={`${SKELETON_SURFACE_CLASS} h-6 w-36`} />
          <div className={`${SKELETON_SURFACE_CLASS} h-8 w-20`} />
        </div>
        <div className="flex w-full items-center justify-between gap-2">
          <div className={`${SKELETON_SURFACE_CLASS} h-[34px] w-36`} />
          <div className={`${SKELETON_SURFACE_CLASS} h-[34px] w-24`} />
        </div>
      </div>
      <div className="flex w-full gap-2 overflow-hidden px-4">
        {MOBILE_OFFER_SKELETON_KEYS.map((key) => (
          <OfferCardSkeleton key={key} />
        ))}
      </div>
    </div>
  </section>
);

export const HomeSurveysSkeleton: FC<{ stacked?: boolean }> = ({ stacked = false }) => (
  <section className="relative w-full pb-6" aria-busy="true" aria-label="Loading surveys">
    <div className="mb-3 hidden flex-col gap-2 md:flex md:flex-row md:items-center md:justify-between">
      <div className={`${SKELETON_SURFACE_CLASS} h-4 w-28`} />
      <div className="grid grid-cols-3 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 md:flex md:gap-1.5">
        {SURVEY_FILTER_SKELETON_KEYS.map((key) => (
          <div key={key} className={`${SKELETON_SURFACE_CLASS} h-11 w-full md:w-[150px]`} />
        ))}
      </div>
    </div>
    <div
      className={classNames(
        stacked
          ? 'grid w-full grid-cols-1 gap-3 md:grid-cols-[repeat(auto-fit,minmax(280px,320px))] md:gap-5'
          : 'flex w-full gap-3 overflow-hidden md:grid md:grid-flow-col md:grid-rows-2 md:auto-cols-[320px] md:gap-5'
      )}
    >
      {SURVEY_CARD_SKELETON_KEYS.map((key) => (
        <SurveyCardSkeleton key={key} fullWidth={stacked} />
      ))}
    </div>
  </section>
);
